import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import { IconChip } from 'components';
import { toEuros } from 'i18n';
import { useMemo } from 'react';
import classNames from 'classnames';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
  },
  title: {
    ...theme.fonts.h5,
    display: 'flex',
    gap: theme.marginBase,
    alignItems: 'center',
  },
  line: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.marginBase * 2,
    marginTop: theme.marginBase * 2,
  },
  column: {
    flexDirection: 'column',
  },
  block: {
    borderRadius: theme.borderRadius.std,
    padding: [[theme.marginBase, theme.marginBase * 2]],
    flex: 1,
    border: {
      style: 'solid',
      width: 1,
      color: theme.colors.grayBorder,
    },
  },
  blockTitle: {
    ...theme.fonts.calendarTitle,
    color: theme.colors.inkSecondary,
  },
  price: {
    ...theme.fonts.body,
  },
  strong: {
    ...theme.fonts.h5,
  },
  taxes: {
    ...theme.fonts.caption,
  },
  taxesStrong: {
    ...theme.fonts.label,
  },
}));

interface Props {
  shouldApplyVat?: boolean;
  prices: number[];
  numberOfParticipants: number;
  column?: boolean;
}

export const MinMaxTotal = ({
  prices,
  shouldApplyVat,
  numberOfParticipants,
  column,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { min, max } = useMemo(() => {
    let min = -1;
    let max = -1;

    for (const total of prices) {
      if (total > max) {
        max = total;
      }
      if (total < min || min === -1) {
        min = total;
      }
    }
    return { min, max };
  }, [prices]);

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <div>{t('billing.studyPrice.subtotal.title')}</div>
        <IconChip icon="ri-user-line">{numberOfParticipants}</IconChip>
      </div>

      <div
        className={classNames(classes.line, {
          [classes.column]: column,
        })}
      >
        <div className={classes.block}>
          <h5 className={classes.blockTitle}>
            {t('billing.studyPrice.subtotal.minimum')}
          </h5>
          {shouldApplyVat ? (
            <>
              <div className={classes.price}>
                <Trans
                  i18nKey="billing.studyPrice.subtotal.exclTax"
                  values={{ amount: min }}
                  components={{
                    strong: <span className={classes.strong} />,
                  }}
                />
              </div>
              <div className={classes.taxes}>
                <Trans
                  i18nKey="billing.studyPrice.subtotal.vat"
                  values={{ amount: min * 0.2 }}
                  components={{
                    strong: <span className={classes.taxesStrong} />,
                  }}
                />
              </div>
            </>
          ) : (
            <div className={classes.price}>
              <span className={classes.strong}>{toEuros(min, true)}</span>
            </div>
          )}
        </div>
        <div className={classes.block}>
          <h5 className={classes.blockTitle}>
            {t('billing.studyPrice.subtotal.maximum')}
          </h5>
          {shouldApplyVat ? (
            <>
              <div className={classes.price}>
                <Trans
                  i18nKey="billing.studyPrice.subtotal.exclTax"
                  values={{ amount: max }}
                  components={{
                    strong: <span className={classes.strong} />,
                  }}
                />
              </div>
              <div className={classes.taxes}>
                <Trans
                  i18nKey="billing.studyPrice.subtotal.vat"
                  values={{ amount: max * 0.2 }}
                  components={{
                    strong: <span className={classes.taxesStrong} />,
                  }}
                />
              </div>
            </>
          ) : (
            <div className={classes.price}>
              <span className={classes.strong}>{toEuros(max, true)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
